import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ContainerTable, LoaderWrapper, TableWrapper } from "./Table.styled";
import DataTable from "react-data-table-component";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import CustomLoader from "../../sharedcomponents/CustomLoader";
import CustomPagination from "../Pagination";
import TextView from "../TextView";
import { useSearchParams } from "react-router-dom";
import NotFound from "../../assets/images/not-found.svg";
import { changeQueryParameters } from "../../utils/helpers";
import Checkbox from "@mui/material/Checkbox";
import { styled } from "@mui/material/styles";

const StyledCheckbox = styled(Checkbox)(({ theme }) => ({
  "&.Mui-checked": {
    color: "#9B5AF8",
  },
}));

const columnDataMapping = {
  Views: "num_views",
  Clones: "num_clones",
  Favorites: "num_favorites",
  Unlocks: "num_unlocks",
};

const Table = ({
  columns,
  data,
  progressPending,
  paginationTotalRows,
  onRowClicked,
  onItemClicked,
  sx,
  onPaginationClicked,
  currentPage = 1,
  isSelectable = false,
  onSelectRows,
  addPaginationToTop = false,
  selectedRows = [], // Add default empty array here
  noPadding = false,
  onRowsPerPageChange,
  rowsPerPage = 10,
  showRecordsCountText = true,
  showPagination = true,
  ...rest
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const order_direction = searchParams.get("order_direction") || "asc";
  const order_by = searchParams.get("order_by") || "";

  const [sortDirection, setSortDirection] = useState(order_direction);
  const [sortColumn, setSortColumn] = useState(order_by);

  useEffect(() => {
    setSortDirection(order_direction || "asc");
    setSortColumn(order_by || "");
  }, [order_direction, order_by]);

  const updatedColumns = useMemo(
    () =>
      columns.map((column) => {
        const clickableColumns = ["Clones", "Favorites", "Views", "Unlocks"];
        const highlightedColumns = ["Clones", "Views", "Unlocks"];

        if (clickableColumns.includes(column.name)) {
          const isHighlighted = highlightedColumns.includes(column.name);
          return {
            ...column,
            cell: (row) => {
              const handleClick = () => {
                onItemClicked(column.name, row?.id);
              };
              return (
                <TextView
                  style={{
                    textDecoration: isHighlighted ? "underline" : "none",
                    cursor: "pointer",
                    color: isHighlighted ? "#0F52BA" : "#123051",
                  }}
                  onClick={handleClick}
                >
                  {row[columnDataMapping[column.name]] ?? 0}
                </TextView>
              );
            },
          };
        }
        return column;
      }),
    [columns, onItemClicked]
  );

  const handleSort = async (column, sortDirection) => {
    const nextDirection = sortDirection === "asc" ? "desc" : "asc";
    setSortDirection(nextDirection);
    setSortColumn(column.sortField);
  };

  useEffect(() => {
    if (sortColumn) {
      const queryParams = changeQueryParameters(searchParams, {
        order_by: sortColumn,
        order_direction: sortDirection,
        offset: 0,
      });
      setSearchParams(queryParams);
    }
  }, [sortColumn, sortDirection]);

  const handleSelectedRowsChange = useCallback(
    (state) => {
      onSelectRows?.(state.selectedRows);
    },
    [onSelectRows]
  );

  return (
    <ContainerTable sx={sx}>
      <TableWrapper noPadding={noPadding}>
        {paginationTotalRows > 0 && addPaginationToTop && !progressPending && (
          <CustomPagination
            currentPage={currentPage}
            onPaginationClicked={onPaginationClicked}
            totalRecords={paginationTotalRows}
            rowsPerPage={rowsPerPage}
          />
        )}
        {progressPending ? (
          <LoaderWrapper
            style={{
              minHeight: `${Math.min(
                Math.max(data.length * 50 + 100, 200),
                500
              )}px`,
              paddingTop: "80px",
            }}
          >
            <CustomLoader />
          </LoaderWrapper>
        ) : !data.length ? (
          <LoaderWrapper>
            <img src={NotFound} width={120} height={120} />
            <TextView style={{ marginBottom: 20 }} variant="title">
              No Data here
            </TextView>
          </LoaderWrapper>
        ) : (
          <>
            {showRecordsCountText && (
              <div
                style={{
                  padding: "0px 0px 20px 0px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <TextView style={{ color: "#123051" }} variant="subtitle">
                  Showing{" "}
                  <span style={{ fontWeight: 700 }}>
                    {Math.min(
                      (currentPage - 1) * rowsPerPage + 1,
                      paginationTotalRows
                    )}{" "}
                    - {Math.min(currentPage * rowsPerPage, paginationTotalRows)}{" "}
                  </span>
                  <span> out of </span>
                  <span style={{ fontWeight: 700 }}>{paginationTotalRows}</span>
                  <span> results</span>
                </TextView>
              </div>
            )}
            <DataTable
              className="custom-analytics-table"
              columns={updatedColumns}
              data={data}
              progressPending={progressPending}
              progressComponent={
                <LoaderWrapper>
                  <CustomLoader />
                </LoaderWrapper>
              }
              noDataComponent={
                <LoaderWrapper>
                  <img src={NotFound} alt="notFound" width={120} height={120} />
                  <TextView style={{ marginBottom: 20 }} variant="title">
                    No Data here
                  </TextView>
                </LoaderWrapper>
              }
              // pagination
              // paginationServer
              // paginationTotalRows={paginationTotalRows}
              // onChangePage={handlePageChange}
              // onChangeRowsPerPage={handlePerRowsChange}
              // paginationComponentOptions={{
              //   rowsPerPageText: "Rows per page:",
              //   rangeSeparatorText: "of",
              //   noRowsPerPage: true,
              // }}
              pointerOnHover={onRowClicked ? true : false}
              onRowClicked={onRowClicked}
              fixedHeader
              persistTableHead
              onSort={handleSort}
              sortIcon={
                <ArrowDownwardIcon
                  sx={{ color: "#8F9094 !important", opacity: "1 !important" }}
                />
              }
              defaultSortFieldId={sortColumn}
              defaultSortAsc={sortDirection === "asc"}
              sortServer={true}
              selectableRows={isSelectable}
              onSelectedRowsChange={handleSelectedRowsChange}
              selectableRowsComponent={StyledCheckbox}
              selectableRowSelected={(row) => {
                return (
                  selectedRows?.some((selected) => selected.id === row.id) ||
                  false
                );
              }}
              {...rest}
            />
          </>
        )}
      </TableWrapper>
      {showPagination && (paginationTotalRows > 0 && !progressPending) && (
        <CustomPagination
          currentPage={currentPage}
          onPaginationClicked={onPaginationClicked}
          totalRecords={paginationTotalRows}
          rowsPerPage={rowsPerPage}
        />
      )}
    </ContainerTable>
  );
};

export default Table;
